/**
 * LinkedResourcesPane (ADR-0059) — a Core area where the operator CURATES which
 * external catalog resources the app consumes: **Datasets** (`kind="dataset"`) or
 * **Knowledge bases** (`kind="kb"`). The app links existing catalog entries; the
 * access matrix ("Membri e ruoli") then scopes per-role visibility to THIS set —
 * separating "what the app uses" from "who sees it".
 *
 * Master/detail (the shared builder pattern): `view="list"` renders the left
 * sidebar (the linked resources + an "Aggiungi" button); `view="detail"` renders
 * the center — the catalog picker when adding, else the selected resource's info
 * + a Rimuovi. Selection is lifted to the parent (a `__add__` sentinel = "adding").
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Database, FileBarChart2, Plus, Search, Trash2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { appsApi, type LinkedResource } from "@/lib/api-apps";
import { datasetsApi } from "@/lib/api-datasets";
import { knowledgeBasesApi } from "@/lib/api-knowledge-bases";
import { reportTypesApi } from "@/lib/api-reports";

type Kind = "dataset" | "kb" | "report";
/** Sentinel selectedId meaning "the add-from-catalog picker is open". */
export const LINKED_ADD = "__add__";

interface Copy {
  icon: typeof Database;
  title: string;
  addLabel: string;
  listEmpty: string;
  catalogEmpty: string;
  pickTitle: string;
}

const COPY: Record<Kind, Copy> = {
  dataset: {
    icon: Database,
    title: "Dataset",
    addLabel: "Aggiungi dataset",
    listEmpty: "Nessun dataset collegato.",
    catalogEmpty: "Nessun dataset del catalogo disponibile da aggiungere.",
    pickTitle: "Aggiungi un dataset del catalogo",
  },
  kb: {
    icon: BookOpen,
    title: "Knowledge base",
    addLabel: "Aggiungi knowledge base",
    listEmpty: "Nessuna knowledge base collegata.",
    catalogEmpty: "Nessuna knowledge base pubblicata disponibile da aggiungere.",
    pickTitle: "Aggiungi una knowledge base",
  },
  report: {
    icon: FileBarChart2,
    title: "Report",
    addLabel: "Aggiungi report",
    listEmpty: "Nessun tipo di report collegato.",
    catalogEmpty: "Nessun tipo di report disponibile da aggiungere (creali in «Report»).",
    pickTitle: "Aggiungi un tipo di report",
  },
};

interface CatalogEntry {
  id: string;
  name: string;
  description: string | null;
}

/** Canonical query-key suffix per kind (`["apps", appId, <suffix>]`). */
const LINKED_KEY: Record<Kind, string> = {
  dataset: "linked-datasets",
  kb: "linked-kbs",
  report: "linked-report-types",
};

const LIST_FN: Record<Kind, (appId: string) => Promise<LinkedResource[]>> = {
  dataset: appsApi.listLinkedDatasets,
  kb: appsApi.listLinkedKbs,
  report: appsApi.listLinkedReportTypes,
};

const LINK_FN: Record<Kind, (appId: string, id: string) => Promise<LinkedResource[]>> = {
  dataset: appsApi.linkDataset,
  kb: appsApi.linkKb,
  report: appsApi.linkReportType,
};

const UNLINK_FN: Record<Kind, (appId: string, id: string) => Promise<void>> = {
  dataset: appsApi.unlinkDataset,
  kb: appsApi.unlinkKb,
  report: appsApi.unlinkReportType,
};

function useLinkedQuery(appId: string, kind: Kind) {
  return useQuery({
    queryKey: ["apps", appId, LINKED_KEY[kind]],
    queryFn: () => LIST_FN[kind](appId),
  });
}

export function LinkedResourcesPane({
  appId,
  kind,
  view,
  selectedId,
  onSelectId,
}: {
  appId: string;
  kind: Kind;
  view: "list" | "detail";
  selectedId: string | null;
  onSelectId: (id: string | null) => void;
}) {
  return view === "list" ? (
    <LinkedList appId={appId} kind={kind} selectedId={selectedId} onSelectId={onSelectId} />
  ) : (
    <LinkedDetail appId={appId} kind={kind} selectedId={selectedId} onSelectId={onSelectId} />
  );
}

// --- LIST (left sidebar) ---------------------------------------------------

function LinkedList({
  appId,
  kind,
  selectedId,
  onSelectId,
}: {
  appId: string;
  kind: Kind;
  selectedId: string | null;
  onSelectId: (id: string | null) => void;
}) {
  const copy = COPY[kind];
  const Icon = copy.icon;
  const linkedQ = useLinkedQuery(appId, kind);
  const linked = linkedQ.data ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
          <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
          {copy.title}
        </span>
        <button
          type="button"
          aria-label={copy.addLabel}
          onClick={() => onSelectId(LINKED_ADD)}
          className={
            "flex items-center gap-1 rounded-md px-1.5 py-1 text-xs transition " +
            (selectedId === LINKED_ADD
              ? "bg-accent text-accent-foreground"
              : "text-muted-foreground hover:bg-surface hover:text-foreground")
          }
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2} />
          Aggiungi
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {linkedQ.isLoading ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">Caricamento…</p>
        ) : linked.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">{copy.listEmpty}</p>
        ) : (
          <ul className="space-y-0.5">
            {linked.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => onSelectId(r.id)}
                  className={
                    "w-full truncate rounded-md px-2 py-1.5 text-left text-sm transition " +
                    (selectedId === r.id
                      ? "bg-accent text-accent-foreground"
                      : "text-foreground hover:bg-surface")
                  }
                >
                  {r.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// --- DETAIL (center) -------------------------------------------------------

function LinkedDetail({
  appId,
  kind,
  selectedId,
  onSelectId,
}: {
  appId: string;
  kind: Kind;
  selectedId: string | null;
  onSelectId: (id: string | null) => void;
}) {
  const copy = COPY[kind];
  const qc = useQueryClient();
  const linkedKey = ["apps", appId, LINKED_KEY[kind]];
  const linkedQ = useLinkedQuery(appId, kind);
  const linked = linkedQ.data ?? [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: linkedKey });
    // the access matrix reads the linked set + role scopes — keep both fresh
    qc.invalidateQueries({ queryKey: ["apps", appId, "roles"] });
  };

  const link = useMutation({
    mutationFn: (id: string) => LINK_FN[kind](appId, id),
    onSuccess: (_data, id) => {
      invalidate();
      onSelectId(id); // select the freshly-linked resource
    },
  });
  const unlink = useMutation({
    mutationFn: (id: string) => UNLINK_FN[kind](appId, id),
    onSuccess: () => {
      invalidate();
      onSelectId(null);
    },
  });

  if (selectedId === LINKED_ADD) {
    return (
      <CatalogPicker
        kind={kind}
        linked={linked}
        pending={link.isPending}
        onPick={(id) => link.mutate(id)}
        onCancel={() => onSelectId(null)}
      />
    );
  }

  const sel: LinkedResource | undefined = linked.find((r) => r.id === selectedId);
  if (!sel) {
    const Icon = copy.icon;
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
        <Icon className="h-8 w-8 opacity-40" strokeWidth={1.25} />
        <p className="text-sm">Seleziona {copy.title.toLowerCase()} dalla lista, o aggiungine una.</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="rounded-lg border border-border/60 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-foreground">{sel.name}</h3>
            {sel.description && (
              <p className="mt-1 text-xs text-muted-foreground">{sel.description}</p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={unlink.isPending}
            onClick={() => unlink.mutate(sel.id)}
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" strokeWidth={1.75} />
            Rimuovi dall'app
          </Button>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Collegata all'app. La visibilità per ruolo si gestisce in «Membri e ruoli».
        </p>
      </div>
    </div>
  );
}

// --- the add-from-catalog picker (detail, when adding) ---------------------

function CatalogPicker({
  kind,
  linked,
  pending,
  onPick,
  onCancel,
}: {
  kind: Kind;
  linked: LinkedResource[];
  pending: boolean;
  onPick: (id: string) => void;
  onCancel: () => void;
}) {
  const copy = COPY[kind];
  const [search, setSearch] = React.useState("");

  const catalogQ = useQuery<CatalogEntry[]>({
    queryKey: [
      kind === "dataset" ? "datasets" : kind === "kb" ? "knowledge-bases" : "report-types",
      "link-pick",
    ],
    queryFn: async () => {
      if (kind === "dataset") {
        const all = await datasetsApi.list();
        return all
          .filter((d) => d.binding !== "internal" && !d.app_table_id)
          .map((d) => ({ id: d.id, name: d.name, description: d.description ?? null }));
      }
      if (kind === "report") {
        const all = await reportTypesApi.list();
        return all.map((t) => ({ id: t.id, name: t.name, description: t.description ?? null }));
      }
      const all = await knowledgeBasesApi.list();
      return all
        .filter((k) => k.published)
        .map((k) => ({ id: k.id, name: k.name, description: k.description ?? null }));
    },
  });

  const linkedIds = new Set(linked.map((r) => r.id));
  const candidates = (catalogQ.data ?? [])
    .filter((c) => !linkedIds.has(c.id))
    .filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="mx-auto max-w-2xl p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{copy.pickTitle}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Annulla
        </Button>
      </div>
      <div className="mb-2 flex items-center gap-2 rounded-md border border-border bg-background px-2">
        <Search className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca nel catalogo…"
          className="w-full bg-transparent py-2 text-sm text-foreground outline-none"
        />
      </div>
      <div className="space-y-1">
        {catalogQ.isLoading ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">Caricamento…</p>
        ) : candidates.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">{copy.catalogEmpty}</p>
        ) : (
          candidates.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={pending}
              onClick={() => onPick(c.id)}
              className="flex w-full items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2.5 text-left transition hover:bg-surface disabled:opacity-50"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-foreground">{c.name}</span>
                {c.description && (
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {c.description}
                  </span>
                )}
              </span>
              <Plus className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} />
            </button>
          ))
        )}
      </div>
    </div>
  );
}
